import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SwipeToDelete from '@/components/shared/SwipeToDelete';
import { ROUTES } from '@/configs/routes';
import { NoteEntity, getNoteSourceArtifactName, getNoteSourceLabel } from '@/entities/Notes';
import { useSidebarNoteStore } from '@/stores/local/sidebar/SidebarNoteStore';
import { useDevices } from '@/utils/devices';

import BaseItem from '../../common/BaseItem';

type NoteItemProps = {
  className?: string;
  model: NoteEntity;
  actionSlot?: React.ReactNode;
};

const NoteItem: React.FC<NoteItemProps> = ({ className, model, actionSlot }) => {
  const { isPhone } = useDevices();
  const navigate = useNavigate();
  const { id, noteId } = useParams();
  const isActive = noteId === String(model.id);
  const { notes } = useSidebarNoteStore();
  const sourceArtifactName = getNoteSourceArtifactName(model);
  const sourceLabel = getNoteSourceLabel(model.artifactType);
  const isManual = model.artifactType === 'manual';
  const sourceDescription = isManual ? undefined : (sourceArtifactName ?? sourceLabel ?? undefined);

  const handleClick = React.useCallback(() => {
    navigate(ROUTES.organization.notebook.notes.note.create(id as string, String(model.id)), {
      state: {
        detailUpdatedAt: model.updatedAt,
        detailCreatedAt: model.createdAt,
      },
    });
  }, [id, model.createdAt, model.id, model.updatedAt, navigate]);

  const handleDelete = React.useCallback(() => {
    notes.remove(String(model.id));
  }, [model.id, notes]);

  return (
    <SwipeToDelete
      isLoading={notes.popupMeta.isLoading}
      isSuccess={notes.popupMeta.isLoaded}
      enabled={isPhone}
      onDelete={handleDelete}
    >
      <BaseItem
        className={className}
        isActive={isActive}
        title={model.caption}
        text={sourceDescription}
        actionSlot={actionSlot}
        onClick={handleClick}
        withBottomLine
      />
    </SwipeToDelete>
  );
};

export default observer(NoteItem);
